<template>
    <div class="cart-page-canvas">
      <main class="container py-3 py-md-5">
        <div class="row">
          <div class="col-12 col-md-8 mx-auto pb-5">
            
            <!-- 🛒 SECTION A: ACTIVE CART ITEMS LIST STREAM -->
            <div class="cart-items-stack d-flex flex-column gap-3 mb-4">
              <article 
                v-for="item in cartItems" 
                :key="item.id" 
                class="cart-item-card p-3 d-flex justify-content-between position-relative"
              >
                <div class="item-meta-details d-flex gap-3" style="gap: 6px;">
                  <div class="item-image-frame d-flex align-items-center justify-content-center">
                    <img :src="item.imageUrl" :alt="item.name" class="img-fluid item-thumb-img" />
                  </div>
                  <div class="item-typography d-flex flex-column justify-content-between">
                    <div>
                      <h3 class="item-title m-0">{{ item.name }}</h3>
                      <p class="item-subtitle text-muted m-0 mt-1">{{ item.description }}</p>
                    </div>
                    <span class="item-price-tag font-weight-bold">
                        ₦{{ formatPrice(item.price) }}
                    </span>
                  </div>
                </div>
  
                <!-- Right Block Controls: Delete & Quantity Modifiers -->
                <div class="item-action-controls d-flex flex-column justify-content-between align-items-end">
                  <button 
                    class="delete-item-btn p-1" 
                    @click="deleteCartItem(item.id)"
                    :aria-label="'Remove ' + item.name + ' from cart'"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
  
                  <div class="quantity-counter-box d-flex align-items-center justify-content-between">
                    <button class="qty-btn" @click="decrementQty(item.id)" :disabled="item.qty <= 1">-</button>
                    <span class="qty-text font-weight-bold">{{ item.qty }}</span>
                    <button class="qty-btn" @click="incrementQty(item.id)">+</button>
                  </div>
                </div>
              </article>
            </div>
  
            <!-- 📍 SECTION B: MOUNT DELIVERY ADDRESS CARD -->
            <div class="checkout-meta-section mb-4">
              <div class="section-header d-flex justify-content-between align-items-center mb-2">
                <h2 class="section-title-label m-0">DELIVERY ADDRESS</h2>
                <button class="change-action-link-btn" @click="isEditingAddress = !isMountedAddressEdit">
                  {{ isEditingAddress ? 'Cancel' : 'Change' }}
                </button>
              </div>
  
              <!-- Interactive Collapsible Address Form Canvas -->
              <transition name="collapse-form">
                <div v-if="isEditingAddress" class="address-edit-form-canvas p-3 mb-3">
                  <div class="form-group mb-3">
                    <label class="form-label-text">Street Address</label>
                    <input type="text" v-model="addressEditForm.street" class="form-control custom-input-field" placeholder="e.g. 123 Maple Avenue" />
                  </div>
                  <div class="form-group mb-3">
                    <label class="form-label-text">City & State</label>
                    <input type="text" v-model="addressEditForm.cityState" class="form-control custom-input-field" placeholder="e.g. Lagos, Nigeria" />
                  </div>
                  <button class="save-address-btn w-100" @click="saveNewAddress">Update Delivery Target</button>
                </div>
              </transition>
  
              <!-- Display Address View Card -->
              <div class="address-display-card p-3 d-flex align-items-start gap-3">
                <div class="location-pin-icon-wrapper d-flex align-items-center justify-content-center mt-1">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <h4 class="address-main-heading m-0">{{ address.street }}</h4>
                  <p class="address-sub-heading text-muted m-0 mt-1">{{ address.cityState }}</p>
                </div>
              </div>
            </div>
  
            <!-- 💳 SECTION C: PAYMENT METHOD FRAME WORK LAYER -->
            <div class="checkout-meta-section mb-5">
              <h2 class="section-title-label mb-2">PAYMENT METHOD</h2>
              <div class="payment-method-card p-3 d-flex align-items-center justify-content-between">
                <div class="payment-meta-details d-flex align-items-center gap-3">
                  <div class="wallet-icon-frame d-flex align-items-center justify-content-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                      <line x1="2" y1="10" x2="22" y2="10"></line>
                    </svg>
                  </div>
                  <div>
                    <h4 class="payment-title m-0">Pay with SAFE</h4>
                    <p class="wallet-balance-subtext text-muted m-0 mt-1">
                      Balance: <span class="font-weight-bold">₦{{ formatPrice(walletBalance) }}</span>
                    </p>
                  </div>
                </div>
                <button class="ui-toggle-switch" :class="{ 'is-active-on': payWithSafe }" @click="payWithSafe = !payWithSafe">
                  <span class="switch-slider-nob"></span>
                </button>
              </div>
            </div>
  
          </div>
        </div>
      </main>
  
      <!-- 🔒 BOTTOM STICKY SECURE CHECKOUT BAR DOCK -->
      <footer class="mobile-sticky-action-dock p-3 border-top w-100">
        <button 
          class="secure-checkout-master-btn w-100 d-flex align-items-center justify-content-center gap-2"
          :disabled="isProcessingCheckout || cartItems.length === 0"
          @click="dispatchSecureCheckout"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          <span>{{ isProcessingCheckout ? 'Verifying Terminal...' : 'Secure Checkout • ₦' + formatPrice(computedTotalPrice) }}</span>
        </button>
      </footer>
    </div>
  </template>
  
  <script>
  // @ts-nocheck
  import Vue from 'vue';
  
  export default Vue.extend({
    name: 'CartPage',
    data() {
      return {
        payWithSafe: true,
        isEditingAddress: false,
        isProcessingCheckout: false,
        walletBalance: 2450.00,
        exchangeRateToNaira: 1550, // Simulated currency multiplier matching design output parameters
        
        address: {
          street: '123 Maple Avenue',
          cityState: 'Lagos, Nigeria'
        },
        addressEditForm: {
          street: '123 Maple Avenue',
          cityState: 'Lagos, Nigeria'
        },
        
        cartItems: [
          {
            id: 'item-camera',
            name: 'Shelta Pro Camera',
            description: 'Outdoor Security',
            price: 199.00,
            qty: 1,
            imageUrl: 'https://unsplash.com'
          },
          {
            id: 'item-hub',
            name: 'SecureEntry Hub',
            description: 'Base Station',
            price: 85.00,
            qty: 2,
            imageUrl: 'https://unsplash.com'
          }
        ]
      };
    },
    computed: {
      computedTotalPrice() {
        // 1. Calculate dollar subtotal loop
        const dollarSubtotal = this.cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
        // 2. Map structural currency conversions down to Naira matching mockup spec precisely (\$369 * 1550 = ~₦373.50 format setup)
        return dollarSubtotal; 
      }
    },
    methods: {
      formatPrice(val) {
        return val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      incrementQty(itemId) {
        const match = this.cartItems.find(item => item.id === itemId);
        if (match) match.qty++;
      },
      decrementQty(itemId) {
        const match = this.cartItems.find(item => item.id === itemId);
        if (match && match.qty > 1) match.qty--;
      },
      deleteCartItem(itemId) {
        this.cartItems = this.cartItems.filter(item => item.id !== itemId);
      },
      saveNewAddress() {
        if (this.addressEditForm.street.trim() && this.addressEditForm.cityState.trim()) {
          this.address.street = this.addressEditForm.street;
          this.address.cityState = this.addressEditForm.cityState;
          this.isEditingAddress = false;
        }
      },
      dispatchSecureCheckout() {
        this.isProcessingCheckout = true;
        console.log(`[Checkout Trigger]: Initializing terminal lock sequence for total payload: ${this.computedTotalPrice}`);
        
        setTimeout(() => {
          this.isProcessingCheckout = false;
          this.$emit('checkout-submit', {
            items: this.cartItems,
            address: this.address,
            total: this.computedTotalPrice
          });
        }, 1500);
      }
    }
  });
  </script>

<style lang="scss" scoped>
$font-sans: 'Public Sans', system-ui, -apple-system, sans-serif;
$color-brand-blue: #0029b3; // High contrast dark cobalt layout color matching mockup terminal perfectly
$color-card-border: #e2e8f0;
$color-bg-light: #f8fafc;
$color-text-header: #7e8b9b;
$color-dark-main: #0c1a30;
$color-muted-main: #64748b;

.cart-page-canvas {
  font-family: $font-sans;
  background-color: #fafbfc;
  color: $color-dark-main;
  min-height: 100vh;
  box-sizing: border-box;
  padding-bottom: 40px; // Protects content loops against mobile action overlay blocking
}

.section-title-label {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: $color-text-header;
  text-transform: uppercase;
  text-align: left;
}

.cart-item-card {
    margin-bottom: 15px;
}
.address-display-card, .payment-meta-details {
    gap: 10px;
}

/* ─── 🛒 CART ELEMENT CARDS ROW STREAM ─── */
.cart-item-card, 
.address-display-card, 
.payment-method-card,
.address-edit-form-canvas {
  background-color: #ffffff;
  border: 1px solid $color-card-border;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.01);
  box-sizing: border-box;
}

.item-image-frame {
  width: 76px;
  height: 76px;
  background-color: $color-bg-light;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;

  .item-thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.item-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: $color-dark-main;
}

.item-subtitle {
  font-size: 0.85rem;
}

.item-price-tag {
  font-size: 1.15rem;
  color: $color-brand-blue;
  letter-spacing: -0.01em;
}

.delete-item-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #dc2626; // Shifts tracking highlight context red on target delete focus intents
  }
}

/* 🔢 INCREMENT DECREMENT COUNTER BOX BUTTONS */
.quantity-counter-box {
  border: 1px solid $color-card-border;
  background-color: $color-bg-light;
  border-radius: 20px; // Smooth completely capsule-style pill track form matching image precisely
  padding: 2px;
  width: 84px;
  height: 32px;

  .qty-btn {
    background: transparent;
    border: none;
    font-size: 1.1rem;
    font-weight: 500;
    color: $color-dark-main;
    cursor: pointer;
    width: 24px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:disabled {
      opacity: 0.25;
      cursor: not-allowed;
    }
  }

  .qty-text {
    font-size: 0.9rem;
    color: $color-dark-main;
  }
}

/* 📍 DELIVERY ADDRESS CONTROLS LAYOUT */
.change-action-link-btn {
  background: transparent;
  border: none;
  font-weight: 700;
  color: $color-brand-blue;
  font-size: 0.9rem;
  cursor: pointer;
}

.location-pin-icon-wrapper, .wallet-icon-frame {
  width: 42px;
  height: 42px;
  background-color: #eff6ff;
  border-radius: 50%;
  color: $color-brand-blue;
  flex-shrink: 0;
}
.wallet-icon-frame { background-color: #e0e7ff; }

.address-main-heading, .payment-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $color-dark-main;
}

.address-sub-heading, .wallet-balance-subtext {
  font-size: 0.9rem;
}

/* 📝 INTERACTIVE ADDRESS EDITOR PANEL SLIDER */
.address-edit-form-canvas {
  background-color: $color-bg-light;
  border-color: rgba(24, 56, 131, 0.15);

  .form-label-text {
    font-size: 0.8rem;
    font-weight: 700;
    color: $color-muted-main;
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .custom-input-field {
    height: 42px;
    border-radius: 8px;
    border: 1px solid $color-card-border;
    font-size: 0.9rem;
    outline: none;

    &:focus {
      border-color: $color-brand-blue;
      box-shadow: 0 0 0 3px rgba(0, 41, 179, 0.08);
    }
  }

  .save-address-btn {
    background-color: $color-dark-main;
    color: #ffffff;
    border: none;
    height: 40px;
    border-radius: 8px;
    font-weight: 700;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover { background-color: lighten($color-dark-main, 10%); }
  }
}

/* 💳 SYSTEM FORM SWITCH CONTROLLER SLIDER */
.ui-toggle-switch {
  width: 50px;
  height: 28px;
  background-color: #cbd5e1;
  border: none;
  border-radius: 14px;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease;
  padding: 0;

  &.is-active-on { background-color: $color-brand-blue; }
}

.switch-slider-nob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  background-color: #ffffff;
  border-radius: 50%;
  transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  
  .is-active-on & { transform: translateX(22px); }
}

/* 📱 SECURE FLOATING CHECKOUT DOCK TERMINAL */
.mobile-sticky-action-dock {
  position: sticky;
  bottom: 0;
  left: 0;
  background-color: #ffffff;
  z-index: 100;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.04);
  box-sizing: border-box;
}

.secure-checkout-master-btn {
  background-color: $color-brand-blue;
  color: #ffffff;
  border: none;
  height: 52px;
  border-radius: 12px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) { background-color: lighten($color-brand-blue, 5%); }
  &:disabled { background-color: #94a3b8; cursor: not-allowed; opacity: 0.6; }
}

/* FORM COLLAPSE DROPDOWN TRANSITIONS ANIMATION */
.collapse-form-enter-active, .collapse-form-leave-active {
  transition: all 0.25s ease-out;
}
.collapse-form-enter, .collapse-form-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
